import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { IOrg, IMembership } from "../models/org";
import { useQuery } from "react-query";
import { getMembership, getOrgs } from "../api/org";
import { ObjectId } from "../models";
import { ISubscription } from "../models/subscription";
import { getSubscription } from "../api/payment";

// context type
interface OrgContextType {
	org?: IOrg,
	setOrg:  React.Dispatch<React.SetStateAction<IOrg | undefined>>
	orgs?: IOrg[],
	isLoading: boolean,
	error: unknown,
	getOrgById: (id: ObjectId) => IOrg | undefined,
	membership?: IMembership,
	subscription?: ISubscription
}

// context
export const OrgContext = createContext<OrgContextType | undefined>(undefined);

// provider
export const OrgProvider = ({ children }: { children: ReactNode }) => {
	const [org, setOrg] = useState<IOrg>()

	const {data: orgs, isLoading, error} = useQuery({
		queryKey: ['Organizations'],
		queryFn: getOrgs,
	});

	const {data: membership} = useQuery({
		queryKey: ['Membership', org?.id],
		queryFn: () => getMembership(org!.id),
		enabled: !!org?.id
	});

	const {data: subscription} = useQuery({
		queryKey: ['Subscription', org?.id],
		queryFn: () => getSubscription(org!.id),
		enabled: !!org?.id
	});

	// Function to get an agent by ID
	const getOrgById = (id: string) => {
		return orgs?.find(org => org.id === id);
	};

	useEffect(()=> {
		if(orgs && orgs.length === 1) {
			setOrg(orgs[0])
		}
	}, [orgs])

	return (
		<OrgContext.Provider value={{
			org,
			setOrg, 
			orgs, 
			isLoading, 
			error, 
			getOrgById,
			membership,
			subscription
		}}>
			{children}
		</OrgContext.Provider>
	)
}

// hook
export const useOrgContext = () => {
	const context = useContext(OrgContext);
	if (!context) {
		throw new Error("useOrgContext must be used within a OrgProvider");
	}
	return context;
}